/**
 * DP WALK — DP101 chapter 3, in three dimensions. Her redundancy boundary as a place.
 *
 * FOUNDER, 2026-08-20: "now do the 3d walk along her".
 *
 * ── THIS DRAWS HER MODEL AND NOTHING ELSE ─────────────────────────────────────────────────────
 * 1,415 quads across 38 parts, compiled from her table of offsets, her declared scantlings and her
 * arrangement. Every triangle here came from that file. Nothing is placed to look right, and the
 * geometry PARITY check from dp-realistic is run on load: if the count of what was drawn does not
 * equal the count of what was declared, the view refuses rather than showing an approximate ship.
 *
 * ── WHY A GUIDED WALK RATHER THAN A FREE CAMERA ───────────────────────────────────────────────
 * A free camera lets a learner look at a handsome vessel. Chapter 3's argument is an ORDER — a
 * switchboard, the sets that feed it, the thrusters that hang off it, and then the bulkhead that
 * separates that half from the other — and the walk follows it, because the order is the lesson.
 *
 * ── THE 2D SPLIT REMAINS THE FLOOR (FP-010) ───────────────────────────────────────────────────
 * This mounts inside the chapter-3 lab ABOVE the existing two-column split, never instead of it.
 * WebGL is unavailable on plenty of what a seafarer has aboard, and on a metered connection three
 * megabytes of vendored renderer is a real cost. If anything here fails — no WebGL, no model, a
 * parity mismatch — the walk says why and the split view is already there, unaffected.
 *
 * ── AND IT PUBLISHES WHAT IT IS NOT ───────────────────────────────────────────────────────────
 * Her model carries `known_not_drawn` and `not_modelled`. A three-dimensional view is the most
 * persuasive surface in this course, so those lists are on screen, not in the file.
 */
import { useState, useEffect, useRef } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { bind, stations, WALK_WHY, MODEL_HONESTY } from './dpwalk.mjs';
import { SOURCE_CH3 } from './dplabs.mjs';
import { makeWalk } from './shipwalk3d.mjs';

const THREE_URL = '/vendor/three.module.min.js';
const REALISTIC_URL = '/dp-realistic.mjs';

export default function DpWalkLab({ model, dead = [], deadGenerators = [], failureLabel = null }) {
  const box = useRef(null);
  const [m3, setM3] = useState(null);
  const [step, setStep] = useState(0);
  const [err, setErr] = useState(null);
  const [parity, setParity] = useState(null);
  const engine = useRef(null);

  useEffect(() => {
    let live = true;
    fetch('/api/ship/model?ship=sthir').then((r) => r.json())
      .then((j) => live && setM3(j)).catch((e) => live && setErr(`her model could not be read (${e})`));
    return () => { live = false; };
  }, []);

  const bnd = m3 && model ? bind(m3, model) : null;
  const walk = m3 && model ? stations(m3, model, bnd) : [];
  const here = walk[Math.min(step, Math.max(0, walk.length - 1))] || null;

  /* Dead units are named by DP id; the walk needs PART ids. Derived through the same binding, so a
     unit whose geometry is missing cannot silently fail to go dark — it would already have failed
     the binding assertion. */
  const deadParts = new Set();
  if (bnd) {
    for (const id of [...dead, ...deadGenerators]) {
      const hit = bnd.byUnit.get(id);
      if (hit) deadParts.add(hit.part.id);
    }
    /* Losing a board takes its geometry too — the visual point of the whole walk. */
    for (const b of bnd.boards) {
      const gone = [...bnd.byUnit.values()].filter((x) => x.unit.switchboard === b)
        .every((x) => deadParts.has(x.part.id));
      const anyOn = [...bnd.byUnit.values()].some((x) => x.unit.switchboard === b);
      if (anyOn && gone) { const p = bnd.boardPart.get(b); if (p) deadParts.add(p.id); }
    }
  }

  useEffect(() => {
    if (!m3 || !box.current || engine.current) return;
    let live = true;
    (async () => {
      try {
        const [THREE, R] = await Promise.all([import(/* @vite-ignore */ THREE_URL), import(/* @vite-ignore */ REALISTIC_URL)]);
        if (!live) return;
        /* PARITY BEFORE PIXELS. dp-realistic's own check, run on the model we are about to draw. */
        const g = R.geometryParity(m3);
        setParity(g);
        if (!g.ok) { setErr(`the model does not match its own declaration (declared ${g.declared}, counted ${g.counted})`); return; }
        engine.current = makeWalk(THREE, box.current, m3);
      } catch (e) {
        if (live) setErr(`the renderer is unavailable here (${e && e.message ? e.message : e})`);
      }
    })();
    return () => { live = false; if (engine.current) { engine.current.dispose(); engine.current = null; } };
  }, [m3]);

  useEffect(() => { if (engine.current && here) engine.current.goTo(here, deadParts); },
    [step, here && here.id, [...deadParts].join(',')]);

  if (err) {
    return (
      <div className="lab-task dpw-fallback">
        <div className="g-label">The walk in three dimensions</div>
        <p className="g-body"><Value of={refused(`Not shown here: ${err}.`)} /></p>
        <p className="g-body g-dim">
          The two-column split below is the same boundary and needs nothing but this page. It is the
          floor, not a consolation — WebGL is unavailable on plenty of what is aboard a ship, and on
          a metered connection a vendored renderer is a real cost.
        </p>
      </div>
    );
  }

  /* ── HER MODEL HAS NOT ARRIVED YET, AND THAT IS NOT AN ERROR (2026-08-21) ────────────────────
     Without this line the FIRST render — before the fetch above resolves — reached `m3.known_not_drawn`
     below and threw, and because a throw in a React child unmounts the tree, it took the whole
     chapter with it: DP101 chapter 3 was a white page from the day this lab was written.
     Nothing caught it. The gates exercise the pure modules, `/study` returns 200 because that is the
     shell, and the lab renders in the browser — which is the one place nothing had looked. The
     sibling written after this one, hullwalklab, has exactly this guard; I added it there and never
     came back. gate-labs-render.mjs now loads every declared lab in a real browser for this reason.
     `model` is guarded too: `bind()` and `stations()` above already treat either being absent as
     "not yet", so the only thing missing was somewhere to say so. */
  if (!m3 || !model) {
    return (
      <div className="lab-task">
        <div className="g-label">The walk in three dimensions · her model, drawn</div>
        <p className="g-body g-dim">Reading her declared model…</p>
      </div>
    );
  }

  return (
    <div className="lab-task">
      <div className="g-label">The walk in three dimensions · her model, drawn</div>
      <p className="g-body"><Value of={cited('Redundancy in a DP vessel is a designed answer to a designed question.', SOURCE_CH3)} /></p>
      <p className="g-body g-dim">{WALK_WHY}</p>

      <div ref={box} className="dpw-canvas" />

      {here && (
        <>
          <div className="lab-task-head dpf-picks">
            {walk.map((s, i) => (
              <button key={s.id} className={`g-btn ${i === step ? 'dpb-on' : ''} ${s.id === 'boundary' ? 'dpf-worst' : ''}`} onClick={() => setStep(i)}>{s.name}</button>
            ))}
          </div>
          <p className="g-body dpw-says"><b>{here.name}.</b> {here.says}</p>
          {failureLabel && deadParts.size > 0 && (
            <p className="g-body"><Value of={computed(`${deadParts.size} part(s) dark — ${failureLabel}`)} /></p>
          )}
        </>
      )}

      {/* ── WHAT THE MODEL SAYS IT IS NOT ── */}
      <div className="dpw-honesty">
        <div className="g-label">What this model is not</div>
        <p className="g-body g-dim">{MODEL_HONESTY}</p>
        {parity && <p className="g-body"><Value of={computed(`${parity.counted} quads drawn, ${parity.declared} declared — parity holds`)} /></p>}
        {(m3.known_not_drawn || []).map((s, i) => <p key={`k${i}`} className="g-body"><Value of={absent()} /> {s}</p>)}
        {(m3.not_modelled || []).map((s, i) => <p key={`n${i}`} className="g-body g-dim">{s}</p>)}
        {m3.source && <p className="g-body g-dim"><b>Source:</b> {m3.source}</p>}
      </div>
    </div>
  );
}
